import { useNavigate } from "react-router-dom";

const RINGS = [95, 150, 205];
const X_SCALE = 1.38;
const Y_SCALE = 0.8;

export default function InterestGraph({ data, avatarUrl, initial }) {
  const navigate = useNavigate();
  const nodes = data?.nodes || [];
  const links = data?.links || [];
  if (!nodes.length) return null;

  const W = 680, H = 460, cx = W / 2, cy = H / 2;

  // tidy orbit layout: nodes spread evenly across three rings
  const perRing = RINGS.map((_, r) => nodes.filter((_, i) => i % RINGS.length === r).length);
  const counters = RINGS.map(() => 0);
  const placed = nodes.map((n, i) => {
    const ring = i % RINGS.length;
    const idx = counters[ring]++;
    const total = perRing[ring];
    const angle = (idx / total) * Math.PI * 2 - Math.PI / 2 + ring * 0.55;
    const r = RINGS[ring];
    return { ...n, ring, angle, x: cx + Math.cos(angle) * r * X_SCALE, y: cy + Math.sin(angle) * r * Y_SCALE };
  });
  const byTag = Object.fromEntries(placed.map((p) => [p.tag, p]));
  const maxCount = Math.max(...nodes.map((n) => n.count));
  const radius = (n) => 8 + 13 * (n.count / maxCount);

  // deterministic background stars
  const stars = Array.from({ length: 26 }, (_, i) => {
    const fx = (Math.sin(i * 12.9898) * 43758.5453) % 1;
    const fy = (Math.sin(i * 78.233) * 12345.6789) % 1;
    return {
      x: 34 + Math.abs(fx) * (W - 68),
      y: 26 + Math.abs(fy) * (H - 52),
      s: 0.8 + Math.abs(fx) * 1.5,
    };
  });

  const arcPath = (a, b) => {
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    const dx = mx - cx, dy = my - cy;
    const d = Math.hypot(dx, dy) || 1;
    const k = 16;
    return `M ${a.x} ${a.y} Q ${mx + (dx / d) * k} ${my + (dy / d) * k} ${b.x} ${b.y}`;
  };

  return (
    <div data-testid="interest-graph" className="rounded-3xl glass-card p-1.5 sm:p-3 animate-fade-in">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Constellation map of interests">
        <defs>
          <clipPath id="avatarClip"><circle cx={cx} cy={cy} r="31" /></clipPath>
          <radialGradient id="centerGlow">
            <stop offset="0%" stopColor="#1E40AF" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0" />
          </radialGradient>
        </defs>

        {stars.map((s, i) => (
          <circle
            key={`s${i}`} cx={s.x} cy={s.y} r={s.s} fill="#1E40AF" className="star"
            style={{ animationDelay: `${(i % 7) * 0.6}s` }}
          />
        ))}

        {RINGS.map((r, i) => (
          <ellipse
            key={`o${i}`} cx={cx} cy={cy} rx={r * X_SCALE} ry={r * Y_SCALE}
            fill="none" stroke="#1E40AF" strokeOpacity="0.09" strokeWidth="1" strokeDasharray="2 7"
          />
        ))}

        {links.map((l, i) => {
          const a = byTag[l.a], b = byTag[l.b];
          if (!a || !b) return null;
          return (
            <path key={`l${i}`} d={arcPath(a, b)} fill="none" stroke="#1E40AF" strokeOpacity="0.15" strokeWidth="1" />
          );
        })}

        {placed.map((n) => (
          <line
            key={`r${n.tag}`} x1={cx} y1={cy} x2={n.x} y2={n.y}
            stroke="#1E40AF" strokeOpacity="0.12" strokeWidth="1" strokeDasharray="2 5"
          />
        ))}

        <circle cx={cx} cy={cy} r="86" fill="url(#centerGlow)" />
        <circle cx={cx} cy={cy} r="41" fill="#FFFFFF" stroke="#1E40AF" strokeOpacity="0.2" strokeWidth="1" />
        <circle cx={cx} cy={cy} r="47" fill="none" stroke="#1E40AF" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="1 6" />
        {avatarUrl ? (
          <image
            href={avatarUrl} x={cx - 31} y={cy - 31} width="62" height="62"
            clipPath="url(#avatarClip)" preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <text x={cx} y={cy + 8} textAnchor="middle" fontSize="24" fill="#1E40AF" fontFamily="'Playfair Display', serif">
            {initial}
          </text>
        )}

        {placed.map((n, i) => {
          const r = radius(n);
          const top = i < 3;
          const below = Math.sin(n.angle) >= 0;
          const ly = below ? n.y + r + 16 : n.y - r - 9;
          return (
            <g
              key={n.tag}
              data-testid={`interest-node-${n.tag}`}
              className="const-node cursor-pointer"
              onClick={() => navigate(`/brain?tag=${encodeURIComponent(n.tag)}`)}
              style={{ animationDelay: `${i * 0.5}s` }}
            >
              <g className="const-float" style={{ animationDelay: `${(i % 5) * 0.9}s` }}>
                <circle
                  cx={n.x} cy={n.y} r={r}
                  fill={top ? "#EEF2FF" : "rgba(255,255,255,0.92)"}
                  stroke="#1E40AF"
                  strokeOpacity={top ? 0.55 : 0.3}
                  strokeWidth={top ? 1.6 : 1.1}
                />
                <circle cx={n.x} cy={n.y} r="2.2" fill="#1E40AF" fillOpacity={top ? 0.85 : 0.4} />
              </g>
              <text
                className="const-label"
                x={n.x} y={ly} textAnchor="middle" fontSize="10.5" fontWeight="600"
                fill="#78716C" fontFamily="'Plus Jakarta Sans', sans-serif"
              >
                {n.tag}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
